import React, { useEffect } from "react";
import { useLocation, Navigate, Link } from "react-router-dom";
import { CheckCircle2, CalendarClock, Car, Phone, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { fmtMoney } from "@/lib/promo";
import { CONTACT } from "@/lib/content";
import { trackBookingConversion } from "@/lib/conversions";

// Booking details are handed over via router state and mirrored to
// sessionStorage so a page refresh re-renders the same confirmation WITHOUT
// creating another booking (the booking POST happens once, in QuoteModal).
export const BOOKING_CONFIRMATION_KEY = "wtd_booking_confirmation";

function readStored() {
  try {
    return JSON.parse(sessionStorage.getItem(BOOKING_CONFIRMATION_KEY) || "null");
  } catch {
    return null;
  }
}

export default function BookingConfirmed() {
  const { state } = useLocation();
  const data = state || readStored();

  // Direct visits (no completed booking in this tab) never see this page —
  // keeps Google Ads conversion hits limited to real bookings.
  const valid = data && data.ref;

  // Fire the ad-platform conversion event exactly once per booking
  // (backend-confirmed redirect only; refresh never re-fires).
  useEffect(() => {
    if (valid) trackBookingConversion(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!valid) return <Navigate to="/services" replace />;

  const when = (() => {
    if (!data.time) {
      try {
        const d = new Date(`${data.date}T12:00:00`);
        return `${d.toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} · drop-off time confirmed by us`;
      } catch {
        return `${data.date} · drop-off time confirmed by us`;
      }
    }
    try {
      const d = new Date(`${data.date}T${data.time}:00`);
      return `${d.toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} · ${data.time}`;
    } catch {
      return `${data.date} · ${data.time}`;
    }
  })();

  const services = Array.isArray(data.services) && data.services.length ? data.services : null;
  const addons = (data.addons || []).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0c0c0f] text-white" data-testid="booking-confirmed-page">
      <Seo
        title="Booking Received — WTD Detailing & Tint Edmonton"
        description="Your booking request has been received."
        path="/booking-confirmed"
        noindex
      />
      <Navbar />

      <main className="pt-40 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#1F5EFF]" />
            <span className="font-mono-alt text-[12px] text-[#1F5EFF]">Booking Confirmed</span>
          </div>

          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-full bg-[#1F5EFF]/15 border border-[#1F5EFF]/40 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-7 h-7 text-[#1F5EFF]" />
            </div>
            <div>
              <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter leading-[1.05]">
                Booking received<span className="text-[#1F5EFF] blue-text-glow">.</span>
              </h1>
              <p className="mt-4 text-white/65 text-base md:text-lg leading-relaxed" data-testid="booking-confirmed-message">
                We've received your booking request and will contact you to confirm
                your appointment.
              </p>
            </div>
          </div>

          <div className="mt-12 rounded-2xl bg-[#121216] border border-white/10 p-6 md:p-8" data-testid="booking-confirmed-card">
            <div className="flex items-center justify-between gap-4 pb-5 border-b border-white/10">
              <span className="font-mono-alt text-[10px] text-white/40 uppercase tracking-widest">Booking Reference</span>
              <span className="font-mono-alt text-sm text-white" data-testid="booking-ref">
                {String(data.ref).slice(0, 8).toUpperCase()}
              </span>
            </div>

            {data.vehicle && (
              <div className="flex items-center justify-between gap-4 py-5 border-b border-white/10">
                <span className="flex items-center gap-2 text-white/50 text-sm">
                  <Car className="w-4 h-4" /> Vehicle
                </span>
                <span className="text-white text-sm font-medium text-right" data-testid="booking-vehicle">
                  {data.vehicle}
                </span>
              </div>
            )}

            <div className="py-5 border-b border-white/10">
              <div className="font-mono-alt text-[10px] text-white/40 uppercase tracking-widest mb-3">
                Services & Packages
              </div>
              {services ? (
                <div className="space-y-2" data-testid="booking-services">
                  {services.map((s, i) => (
                    <div key={i}>
                      <div className="flex justify-between gap-4 text-sm">
                        <span className="text-white font-medium">
                          {s.name}{s.option ? ` — ${s.option.label}` : ""}
                        </span>
                        <span className="text-white/70 tabular-nums">
                          {s.price != null ? fmtMoney(s.price) : "Custom"}
                        </span>
                      </div>
                      {s.discount && (
                        <div className="flex justify-between gap-4 text-xs mt-0.5">
                          <span className="text-emerald-300/80 pl-3">
                            {s.discount.type === "grand_opening_main"
                              ? `Grand Opening −${s.discount.percent}%`
                              : `Additional service −${s.discount.percent}%`}
                          </span>
                          <span className="text-emerald-300 tabular-nums">−{fmtMoney(s.discount.amount)}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-white text-sm font-medium" data-testid="booking-services">
                  {data.service_name}
                </div>
              )}
              {addons.length > 0 && (
                <div className="mt-3 space-y-1" data-testid="booking-addons">
                  {addons.map((a, i) => (
                    <div key={i} className="flex justify-between gap-4 text-xs">
                      <span className={a.price < 0 ? "text-emerald-300/80 pl-3" : "text-white/60"}>{a.label}</span>
                      <span className={`tabular-nums ${a.price < 0 ? "text-emerald-300" : "text-white/80"}`}>
                        {a.custom ? "Custom" : a.price < 0 ? `−${fmtMoney(Math.abs(a.price))}` : `+${fmtMoney(a.price)}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-4 py-5 border-b border-white/10">
              <span className="flex items-center gap-2 text-white/50 text-sm">
                <CalendarClock className="w-4 h-4" /> Requested Date & Time
              </span>
              <span className="text-white text-sm font-medium text-right" data-testid="booking-datetime">
                {when}
              </span>
            </div>

            {data.savings > 0 && (
              <div className="flex justify-between gap-4 pt-5 text-sm" data-testid="booking-savings">
                <span className="text-emerald-300 font-medium">You save</span>
                <span className="text-emerald-300 font-medium tabular-nums">−{fmtMoney(data.savings)}</span>
              </div>
            )}
            <div className="flex justify-between gap-4 pt-5">
              <span className="text-white/70 text-sm">Estimated Total</span>
              <span className="text-white font-display text-xl tabular-nums" data-testid="booking-total">
                {data.price_type === "custom"
                  ? "Custom Quote"
                  : data.price_type === "starting"
                    ? `from ${fmtMoney(data.total)}`
                    : data.total != null ? fmtMoney(data.total) : "Custom Quote"}
              </span>
            </div>
            <p className="mt-3 text-white/35 text-[11px] leading-relaxed">
              Final price is confirmed after in-person inspection. Your time slot is
              reserved — we'll reach out shortly to confirm.
            </p>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/services"
              data-testid="booking-confirmed-back"
              className="inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-[#1F5EFF] text-white font-semibold hover:bg-white hover:text-black transition-colors"
            >
              Explore More Services <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${CONTACT.phone.replace(/[^\d+]/g, "")}`}
              data-testid="booking-confirmed-call"
              className="inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full border border-white/25 text-white hover:border-white hover:bg-white/5 transition-colors"
            >
              <Phone className="w-4 h-4" /> Call {CONTACT.phone}
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
